import React, { useState } from "react";
import { tipoPersonalLabel, tipoContratacionLabel } from "../adapters/empleadosAdapter";
import { syncEmpleado } from "../../../../services/contificoService";
import { showToast } from "../../../../utils/showToast";

const tipoPersonalBadge = (tipo) => {
  switch (tipo) {
    case "docente":        return "bg-info-subtle text-info";
    case "administrativo": return "bg-primary-subtle text-primary";
    case "servicios":      return "bg-warning-subtle text-warning";
    default:               return "bg-secondary-subtle text-secondary";
  }
};

const tipoContratacionBadge = (tipo) => {
  switch (tipo) {
    case "nomina":    return "bg-success-subtle text-success";
    case "servicios": return "bg-info-subtle text-info";
    case "hibrido":   return "bg-warning-subtle text-warning";
    default:          return "bg-secondary-subtle text-secondary";
  }
};

export default function EmpleadosTable({
  empleados,
  loading,
  error,
  reload,
  onSynced,
  seleccionadoId,
  onSelect,
  onEdit,
  onDelete,
  canEdit = true,
  canDelete = true,
  showSync = false,
  maxHeight = "60vh",
}) {
  const [syncingId, setSyncingId] = useState(null);

  const handleSync = async (e, emp) => {
    e.stopPropagation();
    setSyncingId(emp.id);
    try {
      const res = await syncEmpleado(emp.id);
      showToast({ type: "success", message: `Empleado ${res.accion} en Contífico correctamente.` });
      onSynced?.(emp, res);
    } catch (err) {
      showToast({ type: "danger", message: err.message || "Error al sincronizar con Contífico." });
    } finally {
      setSyncingId(null);
    }
  };
  if (loading) {
    return (
      <div className="management-card text-center py-5">
        <div className="spinner-border text-primary" role="status" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="management-card management-alert d-flex justify-content-between align-items-center">
        <span>{error}</span>
        {reload && (
          <button className="btn btn-outline-secondary btn-sm" onClick={reload}>Reintentar</button>
        )}
      </div>
    );
  }

  if (!empleados.length) {
    return (
      <div className="management-card dashboard-empty text-center py-5">
        <i className="bi bi-people dashboard-empty-icon mb-3" />
        <h5 className="fw-semibold mb-1">No hay empleados registrados.</h5>
        <p className="text-muted mb-0">Registra un empleado o ajusta los filtros.</p>
      </div>
    );
  }

  return (
    <div className="management-card rrhh-table-card">
      <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
        <div>
          <p className="management-label mb-1">Listado</p>
          <h6 className="mb-0">Empleados registrados</h6>
        </div>
        {reload && (
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={reload} disabled={loading}>
            <i className="bi bi-arrow-repeat me-2" />
            Actualizar
          </button>
        )}
      </div>

      <div className="management-table-wrapper" style={{ maxHeight }}>
        <table className="table align-middle management-table rrhh-table mb-0">
          <thead className="bg-transparent">
            <tr>
              <th>#</th>
              <th>Empleado</th>
              <th>Cédula</th>
              <th>Puesto</th>
              <th>Tipo personal</th>
              <th>Contratación</th>
              <th className="text-center">Estado</th>
              <th className="text-end">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {empleados.map((emp, idx) => {
              const rowKey = emp.id ? `emp-${emp.id}` : `emp-temp-${idx}`;
              const isSelected = seleccionadoId === emp.id;
              return (
                <tr
                  key={rowKey}
                  className={isSelected ? "selected-row" : ""}
                  onClick={() => onSelect?.(emp)}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onSelect?.(emp); } }}
                  role="button"
                  tabIndex={0}
                >
                  <td className="fw-semibold">{(idx + 1)}</td>
                  <td>
                    <div className="fw-semibold d-flex align-items-center gap-2">
                      {emp.apellidos}, {emp.nombres}
                      {showSync && (
                        syncingId === emp.id
                          ? <span className="spinner-border spinner-border-sm text-primary" />
                          : emp.contificoPersonaId
                            ? <i className="bi bi-cloud-check-fill text-success" title={`Sync: ${emp.contificoPersonaId}`} style={{ fontSize: 12 }} />
                            : <i className="bi bi-cloud-slash text-muted" title="Sin sync" style={{ fontSize: 12 }} />
                      )}
                    </div>
                    <small className="text-muted">{emp.email || emp.telefono || "—"}</small>
                  </td>
                  <td>{emp.cedula || "—"}</td>
                  <td>{emp.puestoNombre || <span className="text-muted">Sin puesto</span>}</td>
                  <td>
                    <span className={`badge rounded-pill ${tipoPersonalBadge(emp.tipoPersonal)}`}>
                      {tipoPersonalLabel(emp.tipoPersonal)}
                    </span>
                  </td>
                  <td>
                    <span className={`badge rounded-pill ${tipoContratacionBadge(emp.tipoContratacion)}`}>
                      {tipoContratacionLabel(emp.tipoContratacion)}
                    </span>
                  </td>
                  <td className="text-center">
                    <span className={`badge rounded-pill px-3 py-2 ${emp.estado === "activo" ? "bg-success-subtle text-success" : "bg-secondary-subtle text-muted"}`}>
                      {emp.estado === "activo" ? "Activo" : "Inactivo"}
                    </span>
                  </td>
                  <td>
                    <div className="d-flex justify-content-end gap-2">
                      {showSync && (
                        <button
                          type="button"
                          className="btn btn-outline-primary btn-sm management-action-btn"
                          onClick={(e) => handleSync(e, emp)}
                          disabled={syncingId === emp.id}
                          title="Sincronizar con Contífico"
                        >
                          {syncingId === emp.id
                            ? <span className="spinner-border spinner-border-sm" />
                            : <i className="bi bi-cloud-upload" />
                          }
                        </button>
                      )}
                      {canEdit && (
                        <button
                          type="button"
                          className="btn btn-outline-secondary btn-sm management-action-btn"
                          onClick={(e) => { e.stopPropagation(); onEdit?.(emp); }}
                          title="Editar"
                        >
                          <i className="bi bi-pencil-square" />
                        </button>
                      )}
                      {canDelete && (
                        <button
                          type="button"
                          className="btn btn-outline-danger btn-sm management-action-btn"
                          onClick={(e) => { e.stopPropagation(); onDelete?.(emp); }}
                          title="Dar de baja"
                        >
                          <i className="bi bi-person-dash" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
